import { useState, useEffect, useContext } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { format } from 'date-fns'
import { Accordion, Author } from 'component-library'
import copy from 'copy-to-clipboard'
import { Tooltip } from 'react-tooltip'
import {
  determineProfileDescription,
  determineProfileTitle,
  sendCustomGAEvent,
} from 'shared-utilities'
import MarkdownText from '../MarkdownText/MarkdownText'
import AddDataForm from '../AddDataForm/AddDataForm'
import { DatasetContext } from '../../context'
import { DATASETS_HIDDEN_TAGS } from '../../utils/helpers'

import './DatasetAboutTab.scss'

interface IDatasetFamilyMeta {
  authorNames: readonly string[]
  authors: {
    avatarUrl?: string
    firstName?: string
    handle: string
    about?: string
    headline?: string
    lastName?: string
  }[]
  changelog: string
  citation: string
  license: string
  usingThisDataset: string
  updated: string
  tags: readonly string[]
}

const DatasetAboutTab = ({
  authorNames,
  authors,
  changelog,
  citation,
  license,
  usingThisDataset,
  updated,
  tags,
}: IDatasetFamilyMeta) => {
  const [showCopyConfirmation, setShowCopyConfirmation] =
    useState<boolean>(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowCopyConfirmation(false)
    }, 2000)
    return () => clearTimeout(timer)
  }, [showCopyConfirmation])

  const { user } = useAuth0()
  const { downloadName } = useContext(DatasetContext)

  const copyBibtexToClipboard = () => {
    copy(citation.trim().replaceAll('&nbsp;', '  '))
    setShowCopyConfirmation(true)
    sendCustomGAEvent('copy-citation', {
      event_category: 'dataset',
      event_label: `${downloadName}`,
      value: JSON.stringify({
        profile_id: user?.sub || '',
      }),
    })
  }

  const formatAuthorBio = ({
    about,
    headline,
  }: {
    about?: string
    headline?: string
  }) => {
    const formattedBio = determineProfileDescription({
      headline,
      about,
    })
    return formattedBio.length > 250
      ? `${formattedBio.substring(0, 250)}...`
      : formattedBio
  }

  return (
    <section className="DatasetAboutTab">
      <Accordion
        isExpandedDefault={true}
        itemTitle="Using this dataset"
        itemContent={
          <>
            <MarkdownText text={usingThisDataset} />
            <div>
              <h3>Authors</h3>
              <p>{authorNames.join(', ')}</p>
            </div>
            <div className="flex gap-4 mb-6 md:mb-12 whitespace-nowrap flex-wrap">
              {tags
                .filter(
                  (tag) => !DATASETS_HIDDEN_TAGS.includes(tag.toLowerCase())
                )
                .map((tag, index) => (
                  <span
                    key={`tag-${index}`}
                    className="bg-grey-7 text-black py-1 px-3 rounded-full text-xs"
                  >
                    {tag}
                  </span>
                ))}
            </div>
            <div className="flex flex-col md:flex-row gap-6">
              <div>
                <h4>Updated</h4>
                {format(new Date(updated), 'yyyy-MM-dd')}
              </div>
              <div>
                <h4>License</h4>
                <MarkdownText text={license} />
              </div>
            </div>
          </>
        }
      />
      <hr className="my-12 text-[#B4B4B4]" />
      <Accordion
        isExpandedDefault={true}
        itemTitle="Changelog"
        itemContent={<MarkdownText text={changelog} />}
      />
      <hr className="my-12 text-[#B4B4B4]" />
      <Accordion
        isExpandedDefault={true}
        itemTitle="Cite this dataset"
        itemContent={
          <>
            <Tooltip
              id="copy-bibtex-button"
              data-testid="copy-code-tooltip"
              content="Copied to clipboard"
              isOpen={showCopyConfirmation}
              className="Copy__tooltip"
            />
            <button
              className="DatasetAboutTab__copyBibtex"
              data-tooltip-id="copy-bibtex-button"
              aria-label="copy bibtex to clipboard"
              onClick={copyBibtexToClipboard}
              data-testid="copy-bibtex"
            >
              <i className="bx bx-copy-alt" />
              <pre
                className="bg-[#F5F5F5] whitespace-pre-line p-4 mb-4 overflow-auto"
                dangerouslySetInnerHTML={{ __html: citation.trim() }}
              />
            </button>
            <AddDataForm />
          </>
        }
      />
      <hr className="my-12 text-[#B4B4B4]" />
      {authors.length > 0 && (
        <Accordion
          isExpandedDefault={true}
          itemTitle="About the authors"
          itemContent={
            <>
              {authors.map((author, index) => {
                return (
                  <div key={`dataset-author-${index}`} className="flex mt-4">
                    <Author
                      name={determineProfileTitle({
                        firstName: author.firstName,
                        lastName: author.lastName,
                        handle: author.handle,
                      })}
                      image={author.avatarUrl}
                      bio={formatAuthorBio({
                        about: author.about,
                        headline: author.headline,
                      })}
                      profileUrl={`/profile/${author.handle}`}
                    />
                  </div>
                )
              })}
            </>
          }
        />
      )}
    </section>
  )
}

export default DatasetAboutTab
